import { useEffect, useMemo, useRef, useState } from "react"
import styled from "styled-components"

export interface SearchSelectOption {
  value: string
  label: string
  /** linha secundária (ex.: endereço da unidade). */
  description?: string
  /** miniatura à esquerda (ex.: foto do veículo no seletor de Modelo). */
  image?: string
}

interface SearchSelectProps {
  ariaLabel: string
  value: string
  options: SearchSelectOption[]
  onChange: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  disabled?: boolean
}

/** Acima disso, mostra um campo de busca para filtrar as opções (combobox). */
const SEARCH_THRESHOLD = 7

/** Select estilizado do bloco com busca por digitação quando a lista é grande.
    Sem dependência de CSS externo — self-contained (styled-components). */
export const SearchSelect = ({
  ariaLabel,
  value,
  options,
  onChange,
  placeholder = "Selecione",
  searchPlaceholder = "Buscar…",
  disabled = false,
}: SearchSelectProps) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const rootRef = useRef<HTMLDivElement | null>(null)
  const searchRef = useRef<HTMLInputElement | null>(null)

  const selected = options.find((o) => o.value === value) || null
  const searchable = options.length > SEARCH_THRESHOLD
  const q = query.trim().toLowerCase()
  const filtered = useMemo(
    () =>
      q
        ? options.filter(
            (o) =>
              o.label.toLowerCase().includes(q) ||
              (o.description ?? "").toLowerCase().includes(q),
          )
        : options,
    [q, options],
  )

  useEffect(() => {
    setQuery("")
    if (open && searchable) {
      const id = window.setTimeout(() => searchRef.current?.focus(), 0)
      return () => window.clearTimeout(id)
    }
  }, [open, searchable])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false)
    document.addEventListener("mousedown", onPointerDown)
    document.addEventListener("touchstart", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("mousedown", onPointerDown)
      document.removeEventListener("touchstart", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  const pick = (val: string) => {
    onChange(val)
    setOpen(false)
  }

  return (
    <Root ref={rootRef}>
      <Trigger
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        {selected ? (
          <Texts>
            <Label>{selected.label}</Label>
            {selected.description && <Desc>{selected.description}</Desc>}
          </Texts>
        ) : (
          <Placeholder>{placeholder}</Placeholder>
        )}
        <Caret aria-hidden>▾</Caret>
      </Trigger>

      {open && (
        <Panel role="listbox" aria-label={ariaLabel}>
          {searchable && (
            <SearchRow>
              <SearchInput
                ref={searchRef}
                type="text"
                autoComplete="off"
                placeholder={searchPlaceholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && filtered.length) {
                    e.preventDefault()
                    pick(filtered[0].value)
                  }
                }}
              />
            </SearchRow>
          )}
          {filtered.map((o) => (
            <Option
              key={o.value}
              type="button"
              role="option"
              aria-selected={o.value === value}
              $selected={o.value === value}
              onClick={() => pick(o.value)}
            >
              {o.image && (
                <Thumb>
                  <img src={o.image} alt="" loading="lazy" />
                </Thumb>
              )}
              <Texts>
                <Label>{o.label}</Label>
                {o.description && <Desc>{o.description}</Desc>}
              </Texts>
            </Option>
          ))}
          {filtered.length === 0 && <Empty>Nenhum resultado para “{query}”.</Empty>}
        </Panel>
      )}
    </Root>
  )
}

const Root = styled.div`
  position: relative;
  width: 100%;
`

const Trigger = styled.button`
  width: 100%;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--td-field-border, var(--general-border, #b7c0cd));
  background: var(--td-input-bg, var(--general-background, #f1f5f9));
  color: var(--td-field-text, var(--text-title, #0f172a));
  cursor: pointer;
  text-align: left;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const Thumb = styled.span`
  flex-shrink: 0;
  width: 56px;
  height: 40px;
  border-radius: 6px;
  overflow: hidden;
  background: var(--general-background, #f1f5f9);
  display: grid;
  place-items: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`

const Texts = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
`
const Label = styled.span`
  font-size: 0.875rem;
  color: var(--td-field-text, var(--text-title, #0f172a));
`
const Desc = styled.span`
  font-size: 0.75rem;
  color: var(--text-body, #64748b);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
const Placeholder = styled.span`
  font-size: 0.875rem;
  color: var(--td-field-placeholder, var(--text-body, #94a3b8));
`
const Caret = styled.span`
  color: var(--text-body, #64748b);
`

const Panel = styled.ul`
  position: absolute;
  z-index: 20;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 280px;
  overflow-y: auto;
  margin: 0;
  padding: 4px;
  list-style: none;
  border-radius: 8px;
  border: 1px solid var(--general-border, #b7c0cd);
  background: var(--cards-background, #fff);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
`

const SearchRow = styled.li`
  position: sticky;
  top: 0;
  padding: 4px;
  background: var(--cards-background, #fff);
`
const SearchInput = styled.input`
  width: 100%;
  height: 36px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid var(--td-field-border, var(--general-border, #b7c0cd));
  font-size: 0.875rem;
  color: var(--td-field-text, var(--text-title, #0f172a));

  &::placeholder {
    color: var(--td-field-placeholder, var(--text-body, #94a3b8));
  }
`

const Option = styled.button<{ $selected?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: none;
  border-radius: 6px;
  background: ${({ $selected }) => ($selected ? "var(--general-background, #f1f5f9)" : "transparent")};
  cursor: pointer;
  text-align: left;

  &:hover {
    background: var(--general-background, #f1f5f9);
  }
`

const Empty = styled.li`
  padding: 12px 10px;
  font-size: 0.875rem;
  color: var(--text-body, #64748b);
`
